import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { DNSRecord, DNSRecordInput, DNSRecordType, DNSProvider } from '@tools-set/shared-types'
import Select from './Select'

interface DNSRecordDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (record: DNSRecordInput) => Promise<void>
  record?: DNSRecord | null
  domainId: string
  provider: DNSProvider
  availableProviders?: DNSProvider[]
}

const DNS_RECORD_TYPES = [
  DNSRecordType.A,
  DNSRecordType.AAAA,
  DNSRecordType.CNAME,
  DNSRecordType.MX,
  DNSRecordType.TXT,
  DNSRecordType.NS,
  DNSRecordType.SRV,
  DNSRecordType.CAA,
]

export default function DNSRecordDialog({
  isOpen,
  onClose,
  onSave,
  record,
  domainId,
  provider,
  availableProviders,
}: DNSRecordDialogProps) {
  const [formData, setFormData] = useState<DNSRecordInput>({
    domainId,
    provider,
    type: DNSRecordType.A,
    name: '',
    content: '',
    ttl: 600,
    proxied: false,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (record) {
      setFormData({
        domainId: record.domainId,
        provider: record.provider,
        type: record.type,
        name: record.name,
        content: record.content,
        ttl: record.ttl,
        priority: record.priority,
        proxied: record.proxied,
        comment: record.comment,
      })
    } else {
      setFormData({
        domainId,
        provider,
        type: DNSRecordType.A,
        name: '',
        content: '',
        ttl: 600,
        proxied: false,
      })
    }
  }, [record, domainId, provider])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSaving(true)

    try {
      await onSave(formData)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  const dialogContent = (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* 标题 */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {record ? '编辑 DNS 记录' : '添加 DNS 记录'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSaving}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 提供商选择 (仅在创建新记录且有多个提供商时显示) */}
            {!record && availableProviders && availableProviders.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DNS 提供商 *
                </label>
                <Select
                  value={formData.provider}
                  onChange={(value) => setFormData({ ...formData, provider: value as DNSProvider })}
                  options={availableProviders.map((p) => ({
                    value: p,
                    label: p === DNSProvider.CLOUDFLARE ? 'Cloudflare' : 'DNSPod',
                    icon: p === DNSProvider.CLOUDFLARE ? (
                      <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      </div>
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      </div>
                    )
                  }))}
                />
                <p className="mt-1 text-xs text-gray-500">
                  选择要在哪个 DNS 提供商创建记录
                </p>
              </div>
            )}

            {/* 记录类型 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                记录类型 *
              </label>
              <Select
                value={formData.type}
                onChange={(value) => setFormData({ ...formData, type: value as DNSRecordType })}
                options={DNS_RECORD_TYPES.map((type) => ({
                  value: type,
                  label: type,
                  icon: (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 text-gray-700 text-xs font-mono">
                      {type.charAt(0)}
                    </span>
                  )
                }))}
              />
            </div>

            {/* 主机记录 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                主机记录 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例如: www 或 @ (根域名)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={isSaving}
              />
              <p className="mt-1 text-xs text-gray-500">
                输入子域名，@ 表示根域名
              </p>
            </div>

            {/* 记录值 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                记录值 *
              </label>
              <input
                type="text"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={
                  formData.type === DNSRecordType.A
                    ? '例如: 192.0.2.1'
                    : formData.type === DNSRecordType.CNAME
                    ? '例如: example.com'
                    : '记录值'
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={isSaving}
              />
            </div>

            {/* TTL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TTL (秒) *
              </label>
              <input
                type="number"
                value={formData.ttl}
                onChange={(e) => setFormData({ ...formData, ttl: parseInt(e.target.value) })}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={isSaving}
              />
              <p className="mt-1 text-xs text-gray-500">
                建议值: 600 (10分钟) 或 3600 (1小时)
              </p>
            </div>

            {/* MX 优先级 */}
            {formData.type === DNSRecordType.MX && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  优先级
                </label>
                <input
                  type="number"
                  value={formData.priority || 10}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSaving}
                />
              </div>
            )}

            {/* Cloudflare 代理选项 */}
            {formData.provider === DNSProvider.CLOUDFLARE &&
              (formData.type === DNSRecordType.A || formData.type === DNSRecordType.AAAA || formData.type === DNSRecordType.CNAME) && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="proxied"
                    checked={formData.proxied || false}
                    onChange={(e) => setFormData({ ...formData, proxied: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={isSaving}
                  />
                  <label htmlFor="proxied" className="ml-2 block text-sm text-gray-700">
                    启用 Cloudflare 代理 (CDN)
                  </label>
                </div>
              )}

            {/* 备注 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                备注
              </label>
              <textarea
                value={formData.comment || ''}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="可选的备注信息"
                disabled={isSaving}
              />
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* 按钮 */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSaving}
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )

  return createPortal(dialogContent, document.body)
}
